import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, Star, GraduationCap, Award, Eye, Newspaper, User, Activity, ArrowRight, Trophy, Users, Flame, Lock } from "lucide-react";

const rankName = (v) => (v == null ? "—" : `#${v}`);

// Age tab → graduation year map (confirmed as of the 2026 season).
const AGE_GROUPS = [
  { age: "18U", grad: 2026 },
  { age: "17U", grad: 2027 },
  { age: "16U", grad: 2028 },
  { age: "15U", grad: 2029 },
];

const fmtVal = (v) => (v == null ? "—" : typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(1)) : v);

export default function TrendingOnCBR() {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/trending").then(({ data }) => setT(data)).catch(() => setT(null)); }, []);
  if (!t) return null;

  return (
    <section className="bg-[#0A192F] py-16 lg:py-20" data-testid="trending-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse" />
          <span className="label-overline text-white/50">Live Activity</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
          <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter text-white">Trending on CBR</h2>
          <div className="flex items-center gap-4">
            <p className="text-sm text-white/50 max-w-md">The heartbeat of Canadian baseball — what's happening across the CBR ecosystem, updated automatically.</p>
            <Link to="/movers" data-testid="trending-movers-cta" className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider bg-[#E63946] text-white px-4 py-2 rounded-sm hover:bg-[#c62f3b] transition-colors">Movers & Risers <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {/* New free-preview leaderboard tables (replace New Media, Featured Prospects, Verified Improvements) */}
          <LeaderboardCard kind="players" title="Top 5 Ranked Players" icon={Trophy} seeAll="/rankings" seeAllTestId="trending-seeall-players" />
          <LeaderboardCard kind="teams" title="Top 5 Ranked Teams" icon={Users} seeAll="/live/team-rankings" seeAllTestId="trending-seeall-teams" />
          <LeaderboardCard kind="qab" title="Top 5 QAB Leaders" icon={Flame} seeAll="/leaderboards" seeAllTestId="trending-seeall-qab" />

          {/* Existing activity cards (kept) */}
          <Card title="Biggest Ranking Movers" icon={TrendingUp} card={t.ranking_movers}
            render={(i) => <PlayerRow key={i.id} p={i} right={<span className="text-emerald-400 font-data text-sm">▲ {i.delta}</span>} sub={`${i.scope} ${rankName(i.from_rank)} → ${rankName(i.to_rank)}`} />} />
          <Card title="CBR Rating Improvements" icon={Star} card={t.rating_improvements}
            render={(i) => <PlayerRow key={i.id} p={i} right={<span className="text-emerald-400 font-data text-sm">+{i.delta}</span>} sub={`${i.from} → ${i.to}`} />} />
          <Card title="New Commitments" icon={GraduationCap} card={t.new_commitments}
            render={(i) => <PlayerRow key={i.id} p={i} sub={i.committed_to ? `Committed · ${i.committed_to}` : "Committed"} />} />
          <Card title="Recent Award Winners" icon={Award} card={t.recent_awards}
            render={(i) => <PlayerRow key={i.id + i.award_label} p={i} sub={i.award_label} />} />
          <Card title="Most Watchlisted" icon={Eye} card={t.most_watchlisted}
            render={(i) => <PlayerRow key={i.id} p={i} right={<span className="text-white/60 text-xs">{i.watchlist_count} 👀</span>} sub={[i.primary_position, i.province].filter(Boolean).join(" · ")} />} />
          <NewsCard card={t.latest_news} />
        </div>
      </div>
    </section>
  );
}

// ── Free-preview leaderboard table card (own age tabs + own data fetch) ──
function LeaderboardCard({ kind, title, icon: Icon, seeAll, seeAllTestId }) {
  const [age, setAge] = useState("18U");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const grp = AGE_GROUPS.find((g) => g.age === age) || AGE_GROUPS[0];
    let stale = false;
    setRows(null);
    const done = (r) => { if (!stale) setRows(r); };
    if (kind === "players") {
      api.get("/rankings", { params: { graduation_year: grp.grad, limit: 5 } })
        .then(({ data }) => done((data.rankings || []).slice(0, 5).map((p) => ({
          key: p.id || p.slug, to: `/prospects/${p.slug}`, rank: p.rank,
          photo: p.profile_photo_url, name: `${p.first_name} ${p.last_name}`,
          sub: [p.primary_position, p.province].filter(Boolean).join(" · "), value: p.cbr_overall_rating,
        }))))
        .catch(() => done([]));
    } else if (kind === "teams") {
      api.get("/live/team-rankings", { params: { age_division: age, only_ranked: true } })
        .then(({ data }) => done((data.teams || []).slice(0, 5).map((tm) => ({
          key: tm.id || tm.slug, to: `/live/teams/${tm.slug}`, rank: tm.rank, logo: true,
          photo: tm.effective_logo_url || tm.logo_url, name: tm.name,
          sub: [tm.province, tm.record ? `${tm.record.w}-${tm.record.l}${tm.record.t ? `-${tm.record.t}` : ""}` : null].filter(Boolean).join(" · "),
          value: tm.cbr_team_rating,
        }))))
        .catch(() => done([]));
    } else {
      api.get("/ingame/leaderboard", { params: { category: "hitting", stat: "qab_pct", graduation_year: grp.grad, page_size: 5 } })
        .then(({ data }) => done((data.items || []).slice(0, 5).map((r, i) => ({
          key: r.player_id, to: `/prospects/${r.player?.slug}`, rank: i + 1,
          photo: r.player?.profile_photo_url, name: `${r.player?.first_name} ${r.player?.last_name}`,
          sub: [r.position, r.province].filter(Boolean).join(" · "), value: r.value, valueLabel: "QAB%", pct: true,
        }))))
        .catch(() => done([]));
    }
    return () => { stale = true; };
  }, [age, kind]);

  const loading = rows === null;
  const empty = Array.isArray(rows) && rows.length === 0;
  return (
    <div data-testid={`trending-card-${title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white/[0.03] border border-white/10 rounded-sm p-5 backdrop-blur flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#E63946]" />
        <span className="label-overline text-white/70">{title}</span>
      </div>
      <div className="flex flex-wrap gap-1 mb-3" data-testid={`trending-age-tabs-${kind}`}>
        {AGE_GROUPS.map((g) => (
          <button
            key={g.age}
            data-testid={`trending-age-tab-${kind}-${g.age}`}
            onClick={() => setAge(g.age)}
            className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors ${
              age === g.age ? "bg-[#E63946] text-white" : "bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white"
            }`}
          >
            {g.age}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-white/40 text-sm flex-1"><Activity className="w-4 h-4 animate-pulse" /> Loading…</div>
      ) : empty ? (
        <div className="py-6 text-white/30 text-sm flex-1">No ranked entries yet for {age}.</div>
      ) : (
        <div className="space-y-1 flex-1">
          {rows.map((r) => (
            <Link key={r.key} to={r.to} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0 group">
              <span className="w-5 shrink-0 text-center font-data text-sm text-white/40">{r.rank ?? "—"}</span>
              <div className={`w-9 h-9 overflow-hidden bg-white/10 shrink-0 rounded-sm ${r.logo ? "p-1 grid place-items-center" : ""}`}>
                {r.photo ? <img src={r.photo} alt="" className={r.logo ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"} /> : <div className="w-full h-full grid place-items-center"><User className="w-4 h-4 text-white/30" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate group-hover:text-[#E63946] transition-colors">{r.name}</div>
                {r.sub && <div className="text-[11px] text-white/40 truncate">{r.sub}</div>}
              </div>
              <div className="text-right shrink-0">
                <span className="font-data text-sm text-white/80">{r.pct ? `${Math.round((r.value || 0) * 100)}%` : fmtVal(r.value)}</span>
                {r.valueLabel && <span className="block text-[9px] uppercase tracking-wider text-white/30">{r.valueLabel}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
      <Link to={seeAll} data-testid={seeAllTestId} className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/60 hover:text-white border-t border-white/10 pt-3 transition-colors group">
        <Lock className="w-3 h-3" /> See all <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </Link>
    </div>
  );
}

function Card({ title, icon: Icon, card, render }) {
  const gathering = card?.status === "gathering";
  const items = card?.items || [];
  return (
    <div data-testid={`trending-card-${title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white/[0.03] border border-white/10 rounded-sm p-5 backdrop-blur">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" /><span className="label-overline text-white/70">{title}</span></div>
      {gathering ? (
        <div className="flex items-center gap-2 py-6 text-white/40 text-sm"><Activity className="w-4 h-4 animate-pulse" /> Gathering data…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-white/30 text-sm">No activity yet.</div>
      ) : (
        <div className="space-y-1">{items.map(render)}</div>
      )}
    </div>
  );
}

function PlayerRow({ p, sub, right }) {
  return (
    <Link to={`/prospects/${p.slug}`} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0 group">
      <div className="w-9 h-9 rounded-sm overflow-hidden bg-white/10 shrink-0">
        {p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><User className="w-4 h-4 text-white/30" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white truncate group-hover:text-[#E63946]">{p.first_name} {p.last_name}</div>
        {sub && <div className="text-[11px] text-white/40 truncate">{sub}</div>}
      </div>
      {right || (p.cbr_overall_rating != null && <span className="font-data text-sm text-white/70">{p.cbr_overall_rating}</span>)}
    </Link>
  );
}

function NewsCard({ card }) {
  const items = card?.items || [];
  return (
    <div data-testid="trending-card-latest-news" className="bg-white/[0.03] border border-white/10 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-3"><Newspaper className="w-4 h-4 text-[#E63946]" /><span className="label-overline text-white/70">Latest News</span></div>
      {items.length === 0 ? <div className="py-6 text-white/30 text-sm">No articles yet.</div> : (
        <div className="space-y-1">
          {items.map((a) => (
            <Link key={a.id} to={`/articles/${a.slug}`} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0 group">
              {a.cover_image_url && <div className="w-12 h-9 rounded-sm overflow-hidden bg-white/10 shrink-0"><img src={a.cover_image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="min-w-0"><div className="text-sm font-semibold text-white truncate group-hover:text-[#E63946]">{a.title}</div>{a.category && <div className="text-[11px] text-white/40 uppercase tracking-wider">{a.category}</div>}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
